import React, { useState, useMemo } from "react";
import { X, ZoomIn } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Import gallery images
import gallery1 from "@/assets/images/gallery/gallery-1.png";
import gallery2 from "@/assets/images/gallery/gallery-2.png";
import gallery3 from "@/assets/images/gallery/gallery-3.png";
import gallery4 from "@/assets/images/gallery/gallery-4.png";
import gallery5 from "@/assets/images/gallery/gallery-5.png";
import gallery6 from "@/assets/images/gallery/gallery-6.png";
import gallery7 from "@/assets/images/gallery/gallery-7.png";
import gallery8 from "@/assets/images/gallery/gallery-8.png";
import gallery9 from "@/assets/images/gallery/gallery-9.png";

export default function Gallery() {
  const { ref, visible } = useScrollReveal();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const galleryData = [
    { src: gallery1, category: "driveways", alt: "Concrete driveway project" },
    { src: gallery2, category: "foundations", alt: "Concrete foundation project" },
    { src: gallery3, category: "slabs", alt: "Concrete slab project" },
    { src: gallery4, category: "patios", alt: "Backyard concrete patio project" },
    { src: gallery5, category: "commercial", alt: "Commercial concrete project" },
    { src: gallery6, category: "demolition", alt: "Concrete demolition project" },
    { src: gallery7, category: "driveways", alt: "Concrete sidewalk and walkway" },
    { src: gallery8, category: "slabs", alt: "Concrete repair and resurfacing" },
    { src: gallery9, category: "patios", alt: "Residential concrete driveway and walkway" },
  ];

  const filters = [
    { key: "all", label: "All" },
    { key: "driveways", label: "Driveways" },
    { key: "foundations", label: "Foundations" },
    { key: "slabs", label: "Slabs" },
    { key: "patios", label: "Patios" },
    { key: "commercial", label: "Commercial" },
    { key: "demolition", label: "Demolition" },
  ];

  const items = useMemo(() => {
    if (filter === "all") return galleryData;
    return galleryData.filter((item) => item.category === filter);
  }, [filter]);

  return (
    <section id="projects" className="bg-[hsl(var(--poured))] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-10 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-0.5 bg-[#434951]" />
            <span className="section-label text-[#194d32] text-base">
              OUR WORK
            </span>
            <div className="w-8 h-0.5 bg-[#434951]" />
          </div>
          <h2 className="headline text-4xl sm:text-5xl md:text-6xl text-[#0d1a2d]">
            Built on Results
          </h2>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-5 py-2 font-heading text-sm tracking-wider uppercase rounded-sm transition-all focus-ring ${
                filter === f.key
                  ? "bg-[hsl(var(--navy))] text-white"
                  : "bg-white text-[hsl(var(--charcoal))] border border-[hsl(var(--concrete))] hover:border-[hsl(var(--accent))]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Masonry grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => setLightbox(item)}
              className="group relative w-full mb-4 break-inside-avoid overflow-hidden rounded-sm bg-[hsl(var(--concrete))]/30 focus-ring"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "scale(1)" : "scale(0.96)",
                transition: `all 0.5s ease-out ${i * 0.06}s`
              }}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="block w-full transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[hsl(var(--navy))]/0 group-hover:bg-[hsl(var(--navy))]/40 transition-colors flex items-center justify-center">
                <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 focus-ring rounded"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <div className="max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="block w-full h-auto max-h-[85vh] object-contain mx-auto"
            />
          </div>
        </div>
      )}
    </section>
  );
}